import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  request,
  type V3ApiSuccessData,
  type V3ApiError,
} from 'javascripts/services/v3ApiClient';
import { ProjectDatabaseId } from 'data/v1';
import {
  TaskObject,
  TaskImageObject,
  TaskVideoObject,
} from 'data/v3/data-contracts';
import {
  PROJECT_TASKS_QUERY_KEY,
  GetProjectTasksResponse,
} from './useGetProjectTasks';
import { buildUpdatedAssetProjectTasks } from 'components/projects/projectTasks/hooks/helpers';
import { ASSET_TYPE } from '../constants';
import {
  ASSET_PROJECT_TASKS_QUERY_KEY,
  PROJECT_TASKS_COUNT_QUERY_KEY,
  GetAssetProjectTasksResponse,
  GetTasksCountResponse,
} from './useGetAssetProjectTasks';

type CreateAssetProjectTaskResponse = V3ApiSuccessData<TaskObject>;

type CreateAssetProjectTaskParams = {
  task: {
    details: string;
    position: number;
    uuid?: string;
    index: number;
  };
};

export const useCreateAssetProjectTask = ({
  projectDbId,
  assetDbId,
  assetType,
  onSuccess,
  onError,
}: {
  projectDbId: ProjectDatabaseId;
  assetDbId: TaskImageObject['id'] | TaskVideoObject['id'];
  assetType: string;
  onSuccess?: (data: TaskObject) => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateAssetProjectTaskResponse,
    V3ApiError,
    CreateAssetProjectTaskParams
  >({
    mutationFn: async (params: CreateAssetProjectTaskParams) => {
      const response = await request({
        url: `projects/${projectDbId}/tasks`,
        method: 'post',
        data: {
          task: {
            ...params.task,
            assets: [
              {
                id: Number(assetDbId),
                type: assetType === ASSET_TYPE.PHOTO ? 'Image' : 'Video',
              },
            ],
          },
        },
      });
      return response.data;
    },
    onMutate: async (params: CreateAssetProjectTaskParams) => {
      await queryClient.cancelQueries({
        queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
      });

      const existingTasksData =
        queryClient.getQueryData<GetAssetProjectTasksResponse>([
          ASSET_PROJECT_TASKS_QUERY_KEY,
          assetDbId,
        ]);

      const existingTasks = existingTasksData?.data || [];

      const date = new Date();

      const { task } = params;

      const updatedAssetProjectTasks = buildUpdatedAssetProjectTasks({
        insertIndex: task.index,
        existingTasks,
        newTask: {
          ...task,
          id: `optimisticAssetProjectTask-${date.getTime()}`,
          projectTaskListId: projectDbId,
          completed: false,
          completedAt: null,
          updatedAt: null,
          createdBy: null,
          completedBy: null,
          lastUpdatedBy: null,
          images: [],
          videos: [],
          assignedUsers: [],
        },
      });

      queryClient.setQueryData(
        [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
        () => ({
          data: updatedAssetProjectTasks,
        }),
      );

      // Update project tasks count data
      // Used for asset project tasks as cache may be empty if the user did not navigate to project page
      const projectTasksCountData =
        queryClient.getQueryData<GetTasksCountResponse>([
          PROJECT_TASKS_COUNT_QUERY_KEY,
          projectDbId,
        ]);

      if (projectTasksCountData) {
        const tasksCount = projectTasksCountData?.data.tasksCount || 0;

        queryClient.setQueryData(
          [PROJECT_TASKS_COUNT_QUERY_KEY, projectDbId],
          () => ({
            data: {
              tasksCount: tasksCount + 1,
              tasksCompletedCount:
                projectTasksCountData?.data.tasksCompletedCount,
            },
          }),
        );
      }

      // Update project tasks data
      // Cache may be empty if the user did not navigate to project page
      const projectTasksData =
        queryClient.getQueryData<GetProjectTasksResponse>([
          PROJECT_TASKS_QUERY_KEY,
          projectDbId,
        ]);

      if (projectTasksData) {
        const tasksCount = projectTasksData?.data.tasksCount || 0;

        queryClient.setQueryData(
          [PROJECT_TASKS_QUERY_KEY, projectDbId],
          () => ({
            data: {
              ...projectTasksData,
              tasksCount: tasksCount + 1,
            },
          }),
        );
      }
    },
    onSuccess: ({ data }: { data: TaskObject }) => {
      onSuccess?.(data);
    },
    onError: () => {
      onError?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
      });
      queryClient.invalidateQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });
    },
  });
};

export default useCreateAssetProjectTask;
